import { Fragment, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { PMField } from "../../../utils/PMField";
import { axiosWithToast, mapErrors } from "../../../Utils";
import { ResourceLoading } from "../../../utils/ResourceLoading";
import {
  createAbsenceType,
  fetchAbsenceType,
  updateAbsenceType,
} from "../../../../api/absence/type";
import PMCombobox from "../../../utils/PMCombobox";

export const absenceTypeCodeOptions = [
  {
    id: "1",
    value: "self_certified",
    label: "Egenmelding",
    title: "Egenmelding",
    class: "bg-green-100 text-green-800",
  },
  {
    id: "2",
    value: "child_sick_leave",
    label: "Sykt barn",
    title: "Sykt barn",
    class: "bg-yellow-100 text-yellow-800",
  },
  {
    id: "3",
    value: "vacation",
    label: "Ferie",
    title: "Ferie",
    class: "bg-orange-100 text-orange-800",
  },
  {
    id: "4",
    value: "compensatory_time_off",
    label: "Avspasering",
    title: "Avspasering",
    class: "bg-orange-100 text-orange-800",
  },
  {
    id: "5",
    value: "sick_leave",
    label: "Sykemelding",
    title: "Sykemelding",
    class: "bg-red-100 text-red-800",
  },
  {
    id: "6",
    value: "leave",
    label: "Permisjon",
    title: "Permisjon",
    class: "bg-blue-100 text-blue-800",
  },
];

export default function AbsenceTypeModal(props) {
  const { t } = useTranslation();
  const { open, onClose, itemId, onRefresh } = props;

  const [itemState, setItemState] = useState({});

  const { absence_type, isLoading } = itemState;

  const getItem = (itemId) => {
    setItemState({ ...itemState, isLoading: true });

    fetchAbsenceType(itemId)
      .then((data) => {
        setItemState({ ...itemState, absence_type: data, isLoading: false });
      })
      .catch((err) => {
        setItemState({ ...itemState, error: err, isLoading: false });
      });
  };

  useEffect(() => {
    if (itemId) {
      getItem(itemId);
    } else {
      setItemState({ ...itemState, absence_type: null });
    }
  }, [open]);

  const createOrUpdate = (data) => {
    if (absence_type?.id) {
      return updateAbsenceType(data);
    } else {
      return createAbsenceType(data);
    }
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="w-full sm:max-w-2xl max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                {isLoading ? (
                  <ResourceLoading />
                ) : (
                  <div className="mt-3 sm:mt-5">
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-medium leading-6 text-gray-900"
                    >
                      {itemId
                        ? t("Redigerer fravær type")
                        : t("Oppretter fravær type")}
                    </Dialog.Title>

                    {isLoading ? (
                      "Loading..."
                    ) : (
                      <Formik
                        initialValues={{
                          id: absence_type?.id,
                          title: absence_type?.title,
                          description: absence_type?.description,
                          code: absence_type?.code,
                          max_occurrences_per_year:
                            absence_type?.max_occurrences_per_year || 4,
                          max_duration_days:
                            absence_type?.max_duration_days || 3,
                          requires_approval:
                            absence_type?.requires_approval || true,
                          submit: null,
                        }}
                        validationSchema={Yup.object({
                          title: Yup.string()
                            .max(255)
                            .required(t("title.required")),
                          description: Yup.string().max(500),
                        })}
                        onSubmit={(values, helpers) => {
                          try {
                            axiosWithToast(
                              createOrUpdate(values),
                              (res) => onRefresh(res),
                              onClose,
                              (res) => {
                                setItemState({
                                  ...itemState,
                                  absence_type: res,
                                });
                              }
                            );
                          } catch (err) {
                            console.error(
                              "Server error:",
                              err.response || err.message
                            ); // Log the error response or message
                            toast.error(
                              err.response?.data?.message ||
                                "An unexpected server error occurred."
                            ); // Display a user-friendly error message
                            helpers.setStatus({ success: false });
                            helpers.setErrors({ submit: err.message });
                            helpers.setSubmitting(false);
                          }
                        }}
                      >
                        {(formik) => (
                          <Form>
                            <div className="grid grid-cols-6 gap-6 mt-10">
                              <div className="col-span-6 sm:col-span-3">
                                <PMField
                                  name="title"
                                  type="text"
                                  label="Title"
                                  {...formik}
                                />
                              </div>

                              <div className="col-span-6 sm:col-span-3"></div>

                              <div className="col-span-6 sm:col-span-6">
                                <PMField
                                  name="description"
                                  type="text"
                                  label="Description"
                                  multiline
                                  rows={2}
                                  {...formik}
                                />
                              </div>
                              <div className="col-span-6 sm:col-span-6">
                                <PMCombobox
                                  name="code"
                                  label={t("Fraværs kode")}
                                  options={absenceTypeCodeOptions}
                                  {...formik}
                                />
                              </div>

                              {/* Only show max_duration_days and max_occurrences_per_year for self_certified (code = 1) and leave (code = 6) */}
                              {(formik.values.code === "self_certified" || formik.values.code === "leave") && (
                                <>
                                  <div className="col-span-6 sm:col-span-6">
                                    <PMField
                                      name="max_duration_days"
                                      type="number"
                                      label="Maks varighet dager"
                                      placeholder="Maks varighet dager"
                                      rows={2}
                                      {...formik}
                                    />
                                  </div>

                                  <div className="col-span-6 sm:col-span-6">
                                    <PMField
                                      name="max_occurrences_per_year"
                                      type="number"
                                      label="Maks tilfeller pr. år"
                                      placeholder="Maks tilfeller pr. år"
                                      rows={2}
                                      {...formik}
                                    />
                                  </div>
                                </>
                              )}

                              {/* Show info message for other absence types */}
                              {formik.values.code && formik.values.code !== "self_certified" && formik.values.code !== "leave" && (
                                <div className="col-span-6 sm:col-span-6">
                                  <div className="rounded-md bg-blue-50 p-4">
                                    <div className="flex">
                                      <div className="ml-3">
                                        <h3 className="text-sm font-medium text-blue-800">
                                          Automatisk beregnet
                                        </h3>
                                        <div className="mt-2 text-sm text-blue-700">
                                          {formik.values.code === "vacation" && (
                                            <p>
                                              Feriedager beregnes automatisk basert på ansattens alder: 25 dager (&lt; 60 år) eller 30 dager (≥ 60 år).
                                            </p>
                                          )}
                                          {formik.values.code === "child_sick_leave" && (
                                            <p>
                                              Dager for sykt barn beregnes automatisk basert på antall barn og rettigheter.
                                            </p>
                                          )}
                                          {formik.values.code === "compensatory_time_off" && (
                                            <p>
                                              Avspasering beregnes automatisk basert på faktiske innstående timer.
                                            </p>
                                          )}
                                          {formik.values.code === "sick_leave" && (
                                            <p>
                                              Sykemelding er basert på legeerklæring og har ingen begrensning på antall dager eller forekomster.
                                            </p>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* <div className="col-span-6 sm:col-span-6">
                                <PMSwitch
                                  name="active"
                                  label="Active"
                                  {...formik}
                                />
                              </div> */}

                              <div className="col-span-6 sm:col-span-3 lg:col-span-2"></div>
                            </div>

                            <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                              <button
                                type="button"
                                className="inline-flex justify-center w-20 rounded-md border border-transparent bg-gray-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm"
                                onClick={onClose}
                              >
                                {t("Close")}{" "}
                              </button>

                              <button
                                type="button"
                                className="inline-flex align-right justify-center rounded-md border border-transparent bg-emerald-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm"
                                onClick={formik.submitForm}
                              >
                                {absence_type?.id ? t("Save") : t("Create")}{" "}
                              </button>
                            </div>
                          </Form>
                        )}
                      </Formik>
                    )}
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
